import React from "react"

import { Button } from "@coval/ui/button"
import type { ChartTable } from "@coval/ui/chart-figure"

interface DistributionRow {
  /** The bin's range or the category, as the reader should hear it. */
  label: string
  count: number
}

interface DistributionTableOptions {
  /** Header for the bin or category column. */
  valueHeader: string
  /** Row of the value being inspected, marked in the table. */
  highlightIndex?: number | null
  /** When set, each row gets a Filter button that applies this row's filter. */
  onFilter?: (index: number) => void
  isFilterActive?: (index: number) => boolean
  /** Count across every value, when the rows show only the largest groups. */
  total?: number
}

/** Size of the whole distribution, when the rows show only part of it. */
interface DistributionTotals {
  count: number
  groups: number
}

/** The takeaway of a count distribution: its size, mode, and highlighted bin. */
export function summarizeDistribution(
  rows: DistributionRow[],
  highlightIndex?: number | null,
  totals?: DistributionTotals,
): string | undefined {
  const total = totals?.count ?? rows.reduce((sum, row) => sum + row.count, 0)
  const groups = totals?.groups ?? rows.length
  if (rows.length === 0 || total === 0) {
    return undefined
  }
  const mode = rows.reduce((best, row) => (row.count > best.count ? row : best))
  const share = Math.round((mode.count / total) * 100)
  const shown =
    groups > rows.length ? ` The chart shows the top ${rows.length}.` : ""
  const parts = [
    `${total} value${total === 1 ? "" : "s"} in ${groups} group${groups === 1 ? "" : "s"}; most fall in ${mode.label} (${mode.count}, ${share}%).${shown}`,
  ]
  const highlighted = highlightIndex != null ? rows[highlightIndex] : undefined
  if (highlighted) {
    parts.push(`This result falls in ${highlighted.label}.`)
  }
  return parts.join(" ")
}

/** One row per bin or category, with its count and share of the total. */
export function buildDistributionTable(
  rows: DistributionRow[],
  {
    valueHeader,
    highlightIndex,
    onFilter,
    isFilterActive,
    total: knownTotal,
  }: DistributionTableOptions,
): ChartTable {
  const total = knownTotal ?? rows.reduce((sum, row) => sum + row.count, 0)
  return {
    columns: [
      { key: "label", header: valueHeader },
      { key: "count", header: "Count", numeric: true },
      { key: "share", header: "Share", numeric: true },
      ...(onFilter ? [{ key: "filter", header: "Filter" }] : []),
    ],
    rows: rows.map((row, index) => {
      const active = isFilterActive?.(index) ?? false
      return {
        key: `${index}:${row.label}`,
        cells: {
          label:
            index === highlightIndex ? `${row.label} (this result)` : row.label,
          count: row.count,
          share: total > 0 ? `${Math.round((row.count / total) * 100)}%` : "—",
          filter: onFilter ? (
            <Button
              type="button"
              variant="secondary"
              active={active}
              aria-pressed={active}
              aria-label={`Filter by ${row.label}`}
              onClick={() => onFilter(index)}
            >
              Filter
            </Button>
          ) : null,
        },
      }
    }),
  }
}
